import React from 'react';
import { VisibleModal } from '.';
import { INTEGRATIONS_CONFIG } from './integrationSettingsConfig';
import { handleReconnect, ReconnectHandlers } from './handleReconnect';
import * as translations from './strings';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button } from 'antd';
import { getLangKey } from '../../models/Language';
import styles from './index.module.css';

const strings = translations[getLangKey()];

const integrationEnabled = (integration?: Record<string, any>) =>
  !!(integration?.enabled ?? integration?.isConnected);

const normalizedIntegration = (integration?: Record<string, any>) => {
  if (!integration) return integration;

  return {
    ...integration,
    enabled: integrationEnabled(integration)
  };
};

export const IntegrationModalContent = ({
  visibleModal,
  integrationProps,
  reconnectHandlers
}: {
  visibleModal: VisibleModal | null;
  integrationProps: Record<string, any>;
  reconnectHandlers: ReconnectHandlers;
}) => {
  if (!visibleModal) return null;

  const { organizationId, projectId } = integrationProps;
  const {
    SettingsComponent,
    displayName,
    reconnect,
    propKeys = [],
    hasCommonProps,
    checkEnabled,
    integrationKey,
    checkKey
  } = INTEGRATIONS_CONFIG[visibleModal];
  const settingsProps = Object.fromEntries(
    propKeys.map(key => [key, integrationProps[key]])
  );
  const integrationName = integrationKey || visibleModal.replace('native', '');
  const checkNames = Array.isArray(checkKey)
    ? checkKey
    : [checkKey || integrationName];
  const onReconnect = handleReconnect({
    ...reconnectHandlers,
    organizationId,
    legacyIntegrations: integrationProps.legacyIntegrations,
    integration: visibleModal
  });
  const commonProps = hasCommonProps
    ? {
        projectId,
        organizationId,
        authProviders: integrationProps.authProviders || [],
        integration: normalizedIntegration(
          integrationProps.legacyIntegrations[integrationName]
        )
      }
    : {};

  if (
    !checkEnabled ||
    !checkNames.some(
      checkName =>
        integrationProps[`${checkEnabled}Integrations`][checkName]?.enabled
    )
  ) {
    return <SettingsComponent {...settingsProps} {...commonProps} />;
  }

  const [title, action, changes] =
    displayName === 'Slack'
      ? [strings.upgradeSlack, strings.upgrade, strings.upgradeChanges]
      : [
          strings.reconnectWith(displayName ?? ''),
          strings.reconnect,
          `${strings.standardChanges} ${strings.inBugherd(displayName ?? '')}`
        ];

  return (
    reconnect && (
      <div>
        <div style={{ padding: '24px 5px' }}>
          <b>{title}</b>
          <br />
          <p>{strings.currentlyConnected(integrationName)}</p>

          <div className={styles.jiraReconnect}>
            <ExclamationCircleFilled className={styles.popoverIcon} />
            <div className={styles.jiraReconnectDescription}>
              <b>{strings.impactQuestion}</b>
              <br />
              <p>{changes}</p>
            </div>
          </div>
        </div>
        <Button
          type="primary"
          onClick={onReconnect}
          loading={
            displayName === 'Slack'
              ? reconnectHandlers.isSlackUpgrading
              : undefined
          }
        >
          {action}
        </Button>
      </div>
    )
  );
};
